/**
 * AdminLayoutsPage — Section → Category → Item drill-down management.
 *
 * Route: /oneadvanced-admin/layouts/:contentType
 *
 * Navigation hierarchy:
 *   Sections list  →  Categories in section  →  Items in category
 *   Breadcrumb: [Admin Hub] / [Layout] / [Section] / [Category]
 */

import { ReactNode, useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { makeStyles } from 'tss-react/mui';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Divider from '@mui/material/Divider';
import CircularProgress from '@mui/material/CircularProgress';
import Tooltip from '@mui/material/Tooltip';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import Chip from '@mui/material/Chip';
import Autocomplete from '@mui/material/Autocomplete';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import LabelIcon from '@mui/icons-material/Label';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import HomeIcon from '@mui/icons-material/Home';

import { Page, Header, Content } from '@backstage/core-components';
import { useApi } from '@backstage/core-plugin-api';
import { catalogApiRef, EntityRefLink } from '@backstage/plugin-catalog-react';
import type { Entity } from '@backstage/catalog-model';

import { oneadvancedApiRef } from '../../../../api/OneAdvancedApi';
import { CategoryForm } from './CategoryForm';
import type { ContentType, LayoutCategory, LayoutItem, LayoutSection } from '../../types';

// ── Constants ────────────────────────────────────────────────────────────────

type ViewLevel = 'sections' | 'categories' | 'items';

// ── Styles ───────────────────────────────────────────────────────────────────

const useStyles = makeStyles()((theme) => ({
  // Breadcrumbs
  breadcrumbs: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap' as const,
    gap: 4,
    marginBottom: theme.spacing(2.5),
    fontSize: 13,
    color: theme.palette.text.secondary,
  },
  bcLink: {
    cursor: 'pointer',
    color: theme.palette.primary.main,
    '&:hover': { textDecoration: 'underline' },
    display: 'flex',
    alignItems: 'center',
    gap: 4,
  },
  bcCurrent: {
    color: theme.palette.text.primary,
    fontWeight: 600,
  },
  bcSep: {
    display: 'flex',
    alignItems: 'center',
    color: theme.palette.text.secondary,
  },
  // Level header
  levelHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(2),
  },
  levelTitle: { fontWeight: 700, fontSize: 18 },
  levelSubtitle: {
    fontSize: 13,
    color: theme.palette.text.secondary,
    marginTop: 2,
  },
  // List rows
  listRow: {
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(1.25, 2),
    borderBottom: `1px solid ${theme.palette.divider}`,
    '&:last-child': { borderBottom: 'none' },
  },
  openBtn: {
    flexShrink: 0,
    marginLeft: theme.spacing(1.5),
    whiteSpace: 'nowrap' as const,
    fontWeight: 600,
  },
  rowIcon: {
    marginRight: theme.spacing(1.5),
    color: theme.palette.text.secondary,
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
  },
  rowContent: { flex: 1, minWidth: 0 },
  rowName: {
    fontWeight: 600,
    fontSize: 14,
    whiteSpace: 'nowrap' as const,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  rowMeta: { fontSize: 12, color: theme.palette.text.secondary, marginTop: 2 },
  rowActions: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.25),
    flexShrink: 0,
    marginLeft: theme.spacing(1),
  },
  arrowGroup: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: 0,
    marginRight: theme.spacing(0.5),
  },
  arrowBtn: { padding: 2 },
  rowNumber: {
    fontSize: 11,
    fontWeight: 700,
    color: theme.palette.text.secondary,
    opacity: 0.5,
    minWidth: 22,
    textAlign: 'right' as const,
    marginRight: theme.spacing(0.75),
    flexShrink: 0,
    fontVariantNumeric: 'tabular-nums',
    letterSpacing: '-0.5px',
  },
  accentDot: {
    width: 10,
    height: 10,
    borderRadius: '50%',
    flexShrink: 0,
    marginRight: theme.spacing(1.5),
    display: 'inline-block',
    verticalAlign: 'middle',
  },
  // Items view
  addItemBar: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(1),
    padding: theme.spacing(1.5, 2),
    borderBottom: `1px solid ${theme.palette.divider}`,
    backgroundColor:
      theme.palette.mode === 'dark'
        ? 'rgba(255,255,255,0.03)'
        : theme.palette.grey[50],
  },
  itemRow: {
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(1, 1.5),
    borderBottom: `1px solid ${theme.palette.divider}`,
    '&:last-child': { borderBottom: 'none' },
  },
  itemContent: { flex: 1, minWidth: 0 },
  itemTitle: {
    fontWeight: 600,
    fontSize: 14,
    whiteSpace: 'nowrap' as const,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  itemRef: { fontSize: 11, color: theme.palette.text.secondary },
  emptyState: {
    padding: theme.spacing(6, 4),
    textAlign: 'center' as const,
    color: theme.palette.text.secondary,
  },
  emptyIcon: { fontSize: 48, marginBottom: theme.spacing(1), opacity: 0.3 },
}));

// ── Generic dialogs ───────────────────────────────────────────────────────────

function ConfirmDialog({
  open,
  title,
  body,
  confirmLabel = 'Delete',
  onConfirm,
  onClose,
}: {
  open: boolean;
  title: string;
  body: ReactNode;
  confirmLabel?: string;
  onConfirm: () => void;
  onClose: () => void;
}) {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Typography component="div">{body}</Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={onConfirm} color="secondary" variant="contained">
          {confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function NameDialog({
  open,
  title,
  label,
  initialValue,
  placeholder,
  onSave,
  onClose,
}: {
  open: boolean;
  title: string;
  label: string;
  initialValue?: string;
  placeholder?: string;
  onSave: (value: string) => void;
  onClose: () => void;
}) {
  const [value, setValue] = useState(initialValue ?? '');
  useEffect(() => {
    if (open) setValue(initialValue ?? '');
  }, [open, initialValue]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <TextField
          label={label}
          value={value}
          onChange={e => setValue(e.target.value)}
          fullWidth
          // eslint-disable-next-line jsx-a11y/no-autofocus
          autoFocus
          variant="outlined"
          size="small"
          placeholder={placeholder}
          onKeyDown={e => {
            if (e.key === 'Enter' && value.trim()) onSave(value.trim());
          }}
          style={{ marginTop: 8 }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={() => value.trim() && onSave(value.trim())}
          disabled={!value.trim()}
          color="primary"
          variant="contained"
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Reorder arrow pair ────────────────────────────────────────────────────────

function ReorderArrows({
  idx,
  total,
  onMove,
  classes,
}: {
  idx: number;
  total: number;
  onMove: (dir: 'up' | 'down') => void;
  classes: ReturnType<typeof useStyles>['classes'];
}) {
  return (
    <div className={classes.arrowGroup}>
      <IconButton
        className={classes.arrowBtn}
        size="small"
        disabled={idx === 0}
        onClick={e => { e.stopPropagation(); onMove('up'); }}
      >
        <ArrowUpwardIcon style={{ fontSize: 13 }} />
      </IconButton>
      <IconButton
        className={classes.arrowBtn}
        size="small"
        disabled={idx === total - 1}
        onClick={e => { e.stopPropagation(); onMove('down'); }}
      >
        <ArrowDownwardIcon style={{ fontSize: 13 }} />
      </IconButton>
    </div>
  );
}

export function AdminLayoutsPage() {
  const { classes } = useStyles();
  const { contentType } = useParams<{ contentType: string }>();
  const navigate = useNavigate();
  const api = useApi(oneadvancedApiRef);
  const catalogApi = useApi(catalogApiRef);

  const ct = (contentType as ContentType) ?? 'workflows';
  const label = ct === 'workflows' ? 'Workflows' : 'Docs';
  const entityLabel = ct === 'workflows' ? 'templates' : 'components';

  // ── Navigation ──────────────────────────────────────────────────────────────

  const [view, setView] = useState<ViewLevel>('sections');
  const [selectedSection, setSelectedSection] = useState<string>(''); // section ID
  const [selectedCategory, setSelectedCategory] = useState<LayoutCategory | null>(null);

  // ── Data ────────────────────────────────────────────────────────────────────

  const [loading, setLoading] = useState(true);
  const [sections, setSections] = useState<LayoutSection[]>([]);
  const [categories, setCategories] = useState<LayoutCategory[]>([]);
  const [items, setItems] = useState<LayoutItem[]>([]);
  const [itemsLoading, setItemsLoading] = useState(false);
  const [entityByRef, setEntityByRef] = useState<Record<string, Entity>>({});
  const [catalogEntities, setCatalogEntities] = useState<Entity[]>([]);
  const [itemCountByCategory, setItemCountByCategory] = useState<Record<string, number>>({});

  // ── Section dialog state ─────────────────────────────────────────────────────

  const [newSectionOpen, setNewSectionOpen] = useState(false);
  const [renameSectionTarget, setRenameSectionTarget] = useState<{ id: string; name: string } | null>(null);
  const [deleteSectionTarget, setDeleteSectionTarget] = useState<LayoutSection | null>(null);

  // ── Category dialog state ────────────────────────────────────────────────────

  const [catFormOpen, setCatFormOpen] = useState(false);
  const [editCatTarget, setEditCatTarget] = useState<LayoutCategory | undefined>(undefined);
  const [deleteCatTarget, setDeleteCatTarget] = useState<LayoutCategory | null>(null);

  // ── Item add state ───────────────────────────────────────────────────────────

  const [addItemEntity, setAddItemEntity] = useState<Entity | null>(null);
  const [addingItem, setAddingItem] = useState(false);
  const [addItemError, setAddItemError] = useState('');

  // ── Derived ──────────────────────────────────────────────────────────────────

  const selectedSectionObj = useMemo(
    () => sections.find(s => s.id === selectedSection),
    [sections, selectedSection],
  );

  const categoriesInSection: LayoutCategory[] = useMemo(
    () =>
      categories
        .filter(c => c.sectionId === selectedSection)
        .sort((a, b) => a.sortOrder - b.sortOrder),
    [categories, selectedSection],
  );

  // ── Load data ────────────────────────────────────────────────────────────────

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [cats, sects] = await Promise.all([
        api.layouts.getCategories(ct),
        api.layouts.getSections(ct),
      ]);
      setCategories(cats);
      setSections(sects);
    } finally {
      setLoading(false);
    }
  }, [api, ct]);

  useEffect(() => { loadData(); }, [loadData]);

  useEffect(() => {
    const filter: Record<string, string> =
      ct === 'workflows'
        ? { kind: 'Template' }
        : { kind: 'Component', 'spec.type': 'documentation' };
    catalogApi.getEntities({ filter }).then(r => setCatalogEntities(r.items));
  }, [catalogApi, ct]);

  const loadItems = useCallback(
    async (categoryId: string) => {
      setItemsLoading(true);
      try {
        const its = await api.layouts.getCategoryItems(ct, categoryId);
        setItems(its);
        const refs = its.map(i => i.entityRef);
        if (refs.length > 0) {
          const result = await catalogApi.getEntitiesByRefs({ entityRefs: refs });
          const entityMap: Record<string, Entity> = {};
          refs.forEach((ref, idx) => {
            if (result.items[idx]) entityMap[ref] = result.items[idx]!;
          });
          setEntityByRef(entityMap);
        } else {
          setEntityByRef({});
        }
      } finally {
        setItemsLoading(false);
      }
    },
    [api, catalogApi, ct],
  );

  useEffect(() => {
    if (view === 'items' && selectedCategory) {
      loadItems(selectedCategory.id);
    }
  }, [view, selectedCategory, loadItems]);

  // Prefetch item counts for all categories whenever the categories view is shown
  useEffect(() => {
    let cancelled = false;
    if (view === 'categories' && categoriesInSection.length > 0) {
      Promise.all(
        categoriesInSection.map(cat =>
          api.layouts.getCategoryItems(ct, cat.id).then(its => ({ id: cat.id, count: its.length })),
        ),
      ).then(results => {
        if (!cancelled) {
          const map: Record<string, number> = {};
          for (const { id, count } of results) map[id] = count;
          setItemCountByCategory(prev => ({ ...prev, ...map }));
        }
      });
    }
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, selectedSection]);

  // ── Navigation helpers ───────────────────────────────────────────────────────

  function drillToSection(sectionId: string) {
    setSelectedSection(sectionId);
    setSelectedCategory(null);
    setView('categories');
  }

  function drillToCategory(cat: LayoutCategory) {
    setSelectedCategory(cat);
    setItems([]);
    setAddItemEntity(null);
    setAddItemError('');
    setView('items');
  }

  function backToSections() {
    setView('sections');
    setSelectedSection('');
    setSelectedCategory(null);
  }

  function backToCategories() {
    setView('categories');
    setSelectedCategory(null);
  }

  // ── Section CRUD ─────────────────────────────────────────────────────────────

  async function handleCreateSection(name: string) {
    setNewSectionOpen(false);
    const section = await api.layouts.createSection(ct, { name });
    setSections(prev => [...prev, section]);
    drillToSection(section.id);
  }

  async function handleRenameSection(id: string, newName: string) {
    const updated = await api.layouts.updateSection(ct, id, { name: newName });
    setSections(prev => prev.map(s => (s.id === id ? updated : s)));
    setRenameSectionTarget(null);
  }

  async function handleDeleteSection(section: LayoutSection) {
    await api.layouts.deleteSection(ct, section.id);
    setSections(prev => prev.filter(s => s.id !== section.id));
    setCategories(prev => prev.filter(c => c.sectionId !== section.id));
    setDeleteSectionTarget(null);
    if (selectedSection === section.id) backToSections();
  }

  async function handleMoveSection(idx: number, dir: 'up' | 'down') {
    const swapIdx = dir === 'up' ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= sections.length) return;
    const newOrder = [...sections];
    [newOrder[idx], newOrder[swapIdx]] = [newOrder[swapIdx], newOrder[idx]];
    const orders = newOrder.map((s, i) => ({ id: s.id, sort_order: i }));
    setSections(newOrder.map((s, i) => ({ ...s, sortOrder: i })));
    await api.layouts.reorderSections(ct, orders);
  }

  // ── Category CRUD ────────────────────────────────────────────────────────────

  async function handleSaveCategory(data: any) {
    if (editCatTarget) {
      const updated = await api.layouts.updateCategory(ct, editCatTarget.id, data);
      setCategories(prev => prev.map(c => (c.id === updated.id ? updated : c)));
      if (selectedCategory?.id === updated.id) setSelectedCategory(updated);
    } else {
      const created = await api.layouts.createCategory(ct, data);
      setCategories(prev => [...prev, created]);
    }
    setCatFormOpen(false);
  }

  async function handleDeleteCategory(cat: LayoutCategory) {
    await api.layouts.deleteCategory(ct, cat.id);
    setCategories(prev => prev.filter(c => c.id !== cat.id));
    setDeleteCatTarget(null);
    if (selectedCategory?.id === cat.id) setSelectedCategory(null);
  }

  async function handleMoveCategory(idx: number, dir: 'up' | 'down') {
    const cats = categoriesInSection;
    const swapIdx = dir === 'up' ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= cats.length) return;
    const catA = cats[idx];
    const catB = cats[swapIdx];
    const orders = [
      { id: catA.id, sort_order: catB.sortOrder },
      { id: catB.id, sort_order: catA.sortOrder },
    ];
    setCategories(prev =>
      prev.map(c => {
        if (c.id === catA.id) return { ...c, sortOrder: catB.sortOrder };
        if (c.id === catB.id) return { ...c, sortOrder: catA.sortOrder };
        return c;
      }),
    );
    await api.layouts.reorderCategories(ct, orders);
  }

  // ── Item CRUD ────────────────────────────────────────────────────────────────

  async function handleAddItem() {
    if (!addItemEntity || !selectedCategory) return;
    const ref = `${addItemEntity.kind.toLowerCase()}:${addItemEntity.metadata.namespace ?? 'default'}/${addItemEntity.metadata.name}`;
    setAddingItem(true);
    setAddItemError('');
    try {
      const newItem = await api.layouts.addItem(ct, selectedCategory.id, ref);
      setItems(prev => [...prev, newItem]);
      setAddItemEntity(null);
      setEntityByRef(prev => ({ ...prev, [ref]: addItemEntity }));
    } catch (e: any) {
      setAddItemError(e.message ?? 'Failed to add item');
    } finally {
      setAddingItem(false);
    }
  }

  async function handleRemoveItem(item: LayoutItem) {
    await api.layouts.removeItem(ct, item.categoryId, item.id);
    setItems(prev => prev.filter(i => i.id !== item.id));
  }

  async function handleMoveItem(idx: number, dir: 'up' | 'down') {
    const swapIdx = dir === 'up' ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= items.length) return;
    const newItems = [...items];
    [newItems[idx], newItems[swapIdx]] = [newItems[swapIdx], newItems[idx]];
    const orders = newItems.map((item, i) => ({ id: item.id, sort_order: i }));
    setItems(newItems.map((item, i) => ({ ...item, sortOrder: i })));
    if (selectedCategory) {
      await api.layouts.reorderItems(ct, selectedCategory.id, orders);
    }
  }

  // ── Breadcrumbs ──────────────────────────────────────────────────────────────

  const breadcrumbs = (
    <div className={classes.breadcrumbs}>
      <span
        className={classes.bcLink}
        onClick={() => navigate('/oneadvanced-admin')}
        role="button"
        tabIndex={0}
        onKeyDown={e => e.key === 'Enter' && navigate('/oneadvanced-admin')}
      >
        <HomeIcon style={{ fontSize: 14 }} />
        Admin Hub
      </span>
      <span className={classes.bcSep}>
        <ChevronRightIcon style={{ fontSize: 16 }} />
      </span>
      {view === 'sections' ? (
        <span className={classes.bcCurrent}>{label} Layout</span>
      ) : (
        <span
          className={classes.bcLink}
          onClick={backToSections}
          role="button"
          tabIndex={0}
          onKeyDown={e => e.key === 'Enter' && backToSections()}
        >
          {label} Layout
        </span>
      )}
      {(view === 'categories' || view === 'items') && (
        <>
          <span className={classes.bcSep}>
            <ChevronRightIcon style={{ fontSize: 16 }} />
          </span>
          {view === 'categories' ? (
            <span className={classes.bcCurrent}>{selectedSectionObj?.name}</span>
          ) : (
            <span
              className={classes.bcLink}
              onClick={backToCategories}
              role="button"
              tabIndex={0}
              onKeyDown={e => e.key === 'Enter' && backToCategories()}
            >
              {selectedSectionObj?.name}
            </span>
          )}
        </>
      )}
      {view === 'items' && selectedCategory && (
        <>
          <span className={classes.bcSep}>
            <ChevronRightIcon style={{ fontSize: 16 }} />
          </span>
          <span className={classes.bcCurrent}>{selectedCategory.name}</span>
        </>
      )}
    </div>
  );

  // ── Sections view ────────────────────────────────────────────────────────────

  const sectionsView = (
    <>
      <div className={classes.levelHeader}>
        <div>
          <Typography className={classes.levelTitle}>{label} Layout</Typography>
          <Typography className={classes.levelSubtitle}>
            Click a section to manage its categories.
          </Typography>
        </div>
        <Button
          startIcon={<AddIcon />}
          variant="contained"
          color="primary"
          size="small"
          onClick={() => setNewSectionOpen(true)}
        >
          New Section
        </Button>
      </div>
      <Paper variant="outlined">
        {sections.length === 0 ? (
          <div className={classes.emptyState}>
            <FolderOpenIcon className={classes.emptyIcon} />
            <Typography variant="body2" gutterBottom>
              No sections yet.
            </Typography>
            <Typography variant="body2">
              Create a section to start organising your {entityLabel}.
            </Typography>
          </div>
        ) : (
          [...sections]
            .sort((a, b) => a.sortOrder - b.sortOrder)
            .map((sec, idx, arr) => {
              const catCount = categories.filter(c => c.sectionId === sec.id).length;
              return (
                <div key={sec.id} className={classes.listRow}>
                  <span className={classes.rowNumber}>#{idx + 1}</span>
                  <ReorderArrows
                    idx={idx}
                    total={arr.length}
                    onMove={dir => handleMoveSection(idx, dir)}
                    classes={classes}
                  />
                  <span className={classes.rowIcon}>
                    <FolderOpenIcon style={{ fontSize: 20 }} />
                  </span>
                  <div className={classes.rowContent}>
                    <div className={classes.rowName}>{sec.name}</div>
                    <div className={classes.rowMeta}>
                      {catCount}{' '}
                      {catCount === 1 ? 'category' : 'categories'}
                    </div>
                  </div>
                  <div className={classes.rowActions}>
                    <Tooltip title="Rename section" arrow>
                      <IconButton
                        size="small"
                        onClick={() => setRenameSectionTarget({ id: sec.id, name: sec.name })}
                      >
                        <EditIcon style={{ fontSize: 15 }} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete section and all its categories" arrow>
                      <IconButton
                        size="small"
                        onClick={() => setDeleteSectionTarget(sec)}
                      >
                        <DeleteIcon style={{ fontSize: 15 }} />
                      </IconButton>
                    </Tooltip>
                  </div>
                  <Button
                    className={classes.openBtn}
                    variant="outlined"
                    color="primary"
                    size="small"
                    endIcon={<ChevronRightIcon />}
                    onClick={() => drillToSection(sec.id)}
                  >
                    Categories
                  </Button>
                </div>
              );
            })
        )}
      </Paper>
    </>
  );

  // ── Categories view ──────────────────────────────────────────────────────────

  const categoriesView = (
    <>
      <div className={classes.levelHeader}>
        <div>
          <Typography className={classes.levelTitle}>{selectedSectionObj?.name}</Typography>
          <Typography className={classes.levelSubtitle}>
            Click a category to manage its {entityLabel}.
          </Typography>
        </div>
        <Button
          startIcon={<AddIcon />}
          variant="contained"
          color="primary"
          size="small"
          onClick={() => {
            setEditCatTarget(undefined);
            setCatFormOpen(true);
          }}
        >
          New Category
        </Button>
      </div>
      <Paper variant="outlined">
        {categoriesInSection.length === 0 ? (
          <div className={classes.emptyState}>
            <LabelIcon className={classes.emptyIcon} />
            <Typography variant="body2" gutterBottom>
              No categories in this section yet.
            </Typography>
            <Button
              startIcon={<AddIcon />}
              variant="outlined"
              color="primary"
              size="small"
              style={{ marginTop: 8 }}
              onClick={() => {
                setEditCatTarget(undefined);
                setCatFormOpen(true);
              }}
            >
              Add first category
            </Button>
          </div>
        ) : (
          categoriesInSection.map((cat, idx) => (
            <div key={cat.id} className={classes.listRow}>
              <span className={classes.rowNumber}>#{idx + 1}</span>
              <ReorderArrows
                idx={idx}
                total={categoriesInSection.length}
                onMove={dir => handleMoveCategory(idx, dir)}
                classes={classes}
              />
              {cat.accentColor ? (
                <span
                  className={classes.accentDot}
                  style={{ backgroundColor: cat.accentColor }}
                />
              ) : (
                <span className={classes.rowIcon}>
                  <LabelIcon style={{ fontSize: 18 }} />
                </span>
              )}
              <div className={classes.rowContent}>
                <div className={classes.rowName}>{cat.name}</div>
                <div className={classes.rowMeta}>
                  {cat.description && <span>{cat.description} &middot; </span>}
                  {itemCountByCategory[cat.id] !== undefined
                    ? `${itemCountByCategory[cat.id]} ${itemCountByCategory[cat.id] === 1 ? entityLabel.slice(0, -1) : entityLabel}`
                    : '…'}
                </div>
              </div>
              <div className={classes.rowActions}>
                <Tooltip title="Edit name, description & colours" arrow>
                  <IconButton
                    size="small"
                    onClick={() => {
                      setEditCatTarget(cat);
                      setCatFormOpen(true);
                    }}
                  >
                    <EditIcon style={{ fontSize: 15 }} />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Delete category" arrow>
                  <IconButton
                    size="small"
                    onClick={() => setDeleteCatTarget(cat)}
                  >
                    <DeleteIcon style={{ fontSize: 15 }} />
                  </IconButton>
                </Tooltip>
              </div>
              <Button
                className={classes.openBtn}
                variant="outlined"
                color="primary"
                size="small"
                endIcon={<ChevronRightIcon />}
                onClick={() => drillToCategory(cat)}
              >
                Items
              </Button>
            </div>
          ))
        )}
      </Paper>
    </>
  );

  // ── Items view ───────────────────────────────────────────────────────────────

  const alreadyAdded = useMemo(
    () => new Set(items.map(i => i.entityRef)),
    [items],
  );

  const pickerOptions = useMemo(
    () =>
      catalogEntities.filter(e => {
        const ref = `${e.kind.toLowerCase()}:${e.metadata.namespace ?? 'default'}/${e.metadata.name}`;
        return !alreadyAdded.has(ref);
      }),
    [catalogEntities, alreadyAdded],
  );

  const itemsView = (
    <>
      <div className={classes.levelHeader}>
        <div>
          <Typography className={classes.levelTitle}>
            {selectedCategory?.name}
          </Typography>
          <Typography className={classes.levelSubtitle}>
            Add and reorder {entityLabel} in this category.
          </Typography>
        </div>
        {selectedCategory && (
          <Button
            startIcon={<EditIcon />}
            variant="outlined"
            size="small"
            onClick={() => {
              setEditCatTarget(selectedCategory);
              setCatFormOpen(true);
            }}
          >
            Edit Category
          </Button>
        )}
      </div>

      <Paper variant="outlined">
        {/* Add item */}
        <div className={classes.addItemBar}>
          <Box flex={1}>
            <Autocomplete<Entity>
              options={pickerOptions}
              getOptionLabel={e => e.metadata.title ?? e.metadata.name}
              isOptionEqualToValue={(a, b) => a.metadata.name === b.metadata.name}
              value={addItemEntity}
              onChange={(_e, val) => {
                setAddItemEntity(val);
                setAddItemError('');
              }}
              renderOption={(props, e) => (
                <Box component="li" {...props}>
                  <Typography style={{ fontSize: 13, fontWeight: 600 }}>
                    {e.metadata.title ?? e.metadata.name}
                  </Typography>
                  <Typography style={{ fontSize: 11, opacity: 0.6 }}>
                    {e.metadata.namespace ?? 'default'}/{e.metadata.name}
                  </Typography>
                </Box>
              )}
              renderInput={params => (
                <TextField
                  {...params}
                  label={`Search ${entityLabel} to add`}
                  variant="outlined"
                  size="small"
                  error={!!addItemError}
                  helperText={addItemError || undefined}
                />
              )}
            />
          </Box>
          <Button
            startIcon={
              addingItem ? <CircularProgress size={14} /> : <AddIcon />
            }
            variant="contained"
            color="primary"
            size="small"
            style={{ marginTop: 4, flexShrink: 0 }}
            disabled={!addItemEntity || addingItem}
            onClick={handleAddItem}
          >
            Add
          </Button>
        </div>

        <Divider />

        {/* eslint-disable no-nested-ternary */}
        {itemsLoading ? (
          <Box display="flex" alignItems="center" p={2.5} style={{ gap: 10 }}>
            <CircularProgress size={18} />
            <Typography variant="body2">Loading…</Typography>
          </Box>
        ) : items.length === 0 ? (
          <div className={classes.emptyState}>
            <Typography variant="body2">
              No {entityLabel} in this category yet.
            </Typography>
            <Typography variant="body2" style={{ marginTop: 4 }}>
              Use the search above to add some.
            </Typography>
          </div>
        ) : (
          items.map((item, idx) => {
            const entity = entityByRef[item.entityRef];
            const displayName = entity
              ? entity.metadata.title ?? entity.metadata.name
              : item.entityRef;
            return (
              <div key={item.id} className={classes.itemRow}>
                <span className={classes.rowNumber}>#{idx + 1}</span>
                <ReorderArrows
                  idx={idx}
                  total={items.length}
                  onMove={dir => handleMoveItem(idx, dir)}
                  classes={classes}
                />
                <div className={classes.itemContent}>
                  <div className={classes.itemTitle}>{displayName}</div>
                  {entity ? (
                    <EntityRefLink
                      entityRef={item.entityRef}
                      style={{ fontSize: 11 }}
                    />
                  ) : (
                    <span className={classes.itemRef}>
                      {item.entityRef}{' '}
                      <Chip
                        label="not found"
                        size="small"
                        style={{ fontSize: 10, height: 16 }}
                      />
                    </span>
                  )}
                </div>
                <Tooltip title="Remove from category" arrow>
                  <IconButton
                    size="small"
                    onClick={() => handleRemoveItem(item)}
                  >
                    <DeleteIcon style={{ fontSize: 15 }} />
                  </IconButton>
                </Tooltip>
              </div>
            );
          })
        )}
        {/* eslint-enable no-nested-ternary */}
      </Paper>
    </>
  );

  // ── Render ───────────────────────────────────────────────────────────────────

  return (
    <Page themeId="tool">
      <Header title={`${label} Layout`} subtitle="Manage sections, categories and items." />
      <Content>
        {breadcrumbs}

        {/* eslint-disable no-nested-ternary */}
        {loading ? (
          <Box display="flex" alignItems="center" p={4} style={{ gap: 12 }}>
            <CircularProgress size={24} />
            <Typography>Loading…</Typography>
          </Box>
        ) : view === 'sections' ? (
          sectionsView
        ) : view === 'categories' ? (
          categoriesView
        ) : (
          itemsView
        )}
        {/* eslint-enable no-nested-ternary */}
      </Content>

      {/* New section */}
      <NameDialog
        open={newSectionOpen}
        title="New Section"
        label="Section name"
        placeholder="e.g. Toolchain, Products"
        onSave={handleCreateSection}
        onClose={() => setNewSectionOpen(false)}
      />

      {/* Rename section */}
      <NameDialog
        open={!!renameSectionTarget}
        title={renameSectionTarget ? `Rename "${renameSectionTarget.name}"` : ''}
        label="New name"
        initialValue={renameSectionTarget?.name}
        onSave={newName => renameSectionTarget && handleRenameSection(renameSectionTarget.id, newName)}
        onClose={() => setRenameSectionTarget(null)}
      />

      {/* Delete section */}
      {deleteSectionTarget && (() => {
        const catCount = categories.filter(c => c.sectionId === deleteSectionTarget.id).length;
        return (
          <ConfirmDialog
            open
            title="Delete section?"
            body={
              <>
                Delete <strong>{deleteSectionTarget.name}</strong> and all{' '}
                <strong>{catCount}</strong>{' '}
                {catCount === 1 ? 'category' : 'categories'}{' '}
                within it? This cannot be undone.
              </>
            }
            onConfirm={() => handleDeleteSection(deleteSectionTarget)}
            onClose={() => setDeleteSectionTarget(null)}
          />
        );
      })()}

      {/* Category form */}
      <CategoryForm
        open={catFormOpen}
        existing={editCatTarget}
        defaultSectionId={editCatTarget ? undefined : selectedSection}
        sections={sections}
        onSave={handleSaveCategory}
        onClose={() => setCatFormOpen(false)}
      />

      {/* Delete category */}
      {deleteCatTarget && (
        <ConfirmDialog
          open
          title="Delete category?"
          body={
            <>
              Delete <strong>{deleteCatTarget.name}</strong>? All items in this
              category will also be removed. This cannot be undone.
            </>
          }
          onConfirm={() => handleDeleteCategory(deleteCatTarget)}
          onClose={() => setDeleteCatTarget(null)}
        />
      )}
    </Page>
  );
}
